/**
 * EmbedBridge — Phase 4-2 / T8.
 *
 * Origin-validated postMessage transport between the embedded widget and its
 * host CRM. Before SSO completes, `init` is accepted from any origin (we cannot
 * know the host origin until SSO returns its allowlist). After
 * `lockToOrigin(origin)`, all inbound + outbound traffic is restricted to that
 * single origin.
 *
 * The lock is derived from the `init` message's own `ev.origin` — the
 * browser-supplied, unspoofable identity of the sender. It used to come from
 * `document.referrer`, which the host page can suppress via Referrer-Policy;
 * when it was missing the bridge silently stayed unlocked and broadcast every
 * widget->host message to `'*'`. There is deliberately no fallback now: an
 * unlocked bridge sends nothing except the `ready` handshake.
 *
 * Importers: src/lib/embed/bootstrap.ts (registers handlers, calls start +
 * lockToOrigin). Phase 4-3 components send via the returned bridge.
 */
import { isEnvelope, } from './protocol.js';
export class EmbedBridge {
    constructor() {
        this.allowedOrigins = [];
        this.lockedOrigin = null;
        this.handlers = new Map();
        this.started = false;
        this.listener = null;
    }
    /** Set the origin allowlist (called after SSO with `embedOrigins`). */
    setAllowedOrigins(origins) {
        this.allowedOrigins = origins.slice();
    }
    /** Register a handler for a specific message type. */
    on(type, fn) {
        this.handlers.set(type, fn);
    }
    /** Begin listening for `message` events on window. */
    start() {
        if (this.started)
            return;
        this.listener = (ev) => this.handleMessage(ev);
        window.addEventListener('message', this.listener);
        this.started = true;
    }
    /** Tear down the message listener. */
    stop() {
        if (!this.started)
            return;
        if (this.listener)
            window.removeEventListener('message', this.listener);
        this.listener = null;
        this.started = false;
    }
    /**
     * Send a message to `window.parent`, targeted at the locked origin.
     *
     * Drops the message if no lock has been established. These payloads carry
     * real data — conversationIds on `createHostEntityRequest`, entity ids on
     * `openHostEntity`, operator-facing text on `notify` — and posting them to
     * `'*'` hands them to whatever page happens to be framing us. Silence is
     * the correct failure mode; the only pre-lock outbound is sendHandshake().
     */
    send(msg) {
        if (typeof window === 'undefined' || !window.parent)
            return;
        if (this.lockedOrigin === null) {
            console.warn(`EmbedBridge: dropping '${msg.type}' — bridge is not locked to a host origin`);
            return;
        }
        window.parent.postMessage(msg, this.lockedOrigin);
    }
    /**
     * Send the `ready` handshake before any origin is known.
     *
     * This is the one message that must go out unlocked: the host cannot send
     * `init` — the message we derive the lock from — until it knows we exist.
     * The parameter is typed to ReadyMessage specifically so no data-bearing
     * message can ever reach the `'*'` target through this door. `ready`
     * carries `{ protocolVersion }` and nothing else.
     */
    sendHandshake(msg) {
        if (typeof window === 'undefined' || !window.parent)
            return;
        window.parent.postMessage(msg, this.lockedOrigin ?? '*');
    }
    /**
     * Lock subsequent send + receive to a single origin.
     *
     * When an allowlist is configured, the origin must be in it. An EMPTY
     * allowlist means "no origin restriction configured for this host product"
     * and any concrete origin is accepted — matching how identity treats the
     * same field on the visitor surface (widget-visitor.service.ts). We still
     * lock, so traffic is pinned to one real origin either way; the allowlist
     * only decides whether we additionally enforce *which* one.
     */
    lockToOrigin(origin) {
        // 'null' is what the browser reports for an opaque origin (sandboxed
        // iframe, data: URL). It is not a postMessage target we can address, so
        // locking to it would silently black-hole every send.
        if (!origin || origin === 'null') {
            throw new Error(`EmbedBridge: cannot lock to opaque origin '${origin}'`);
        }
        if (this.allowedOrigins.length > 0 && !this.allowedOrigins.includes(origin)) {
            throw new Error(`EmbedBridge: cannot lock to origin '${origin}' — not in allowlist [${this.allowedOrigins.join(', ')}]`);
        }
        this.lockedOrigin = origin;
    }
    /** Test hook — true if a lock has been applied. */
    isLocked() {
        return this.lockedOrigin !== null;
    }
    /** Test hook — returns the locked origin if any. */
    getLockedOrigin() {
        return this.lockedOrigin;
    }
    handleMessage(ev) {
        // After lock, reject anything not from the locked origin.
        if (this.lockedOrigin !== null && ev.origin !== this.lockedOrigin) {
            return;
        }
        // Before lock we cannot filter on origin — that's what `init` establishes
        // — so filter on the sender instead. Only our own embedder may open the
        // handshake; a sibling frame or a popup holding a handle to this window
        // must not get to be the one that sets the lock. A null source (synthetic
        // events in tests, standalone dev with no embedder) is allowed through:
        // cross-window postMessage always carries a source, so null cannot be
        // forged by another window to bypass this.
        if (this.lockedOrigin === null && ev.source !== null && ev.source !== window.parent) {
            return;
        }
        const msg = ev.data;
        if (!isEnvelope(msg))
            return;
        // Narrow to HostToWidget by type discriminator; protocol-mismatched
        // messages just silently drop here.
        const handler = this.handlers.get(msg.type);
        if (!handler)
            return;
        handler(msg, ev.origin);
    }
}
/** Helper — generate a short envelope id without a uuid dependency. */
export function newEnvelopeId() {
    // 12 hex chars from Math.random — fine for correlation; not crypto.
    return Math.random().toString(16).slice(2, 14).padEnd(12, '0');
}
//# sourceMappingURL=bridge.js.map